import React, { useEffect, useRef, useState, useCallback } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';

const API_BASE = (axiosInstance.defaults.baseURL ?? '').replace(/\/$/, '');

// ─── Types ────────────────────────────────────────────────────────────────────
interface Etiket { etiketID: number; ad: string; renk: string; }
interface KullaniciOzet { kullaniciID: number; adSoyad: string; }
interface PaylasimKayit { id: number; paylasilanKullaniciID: number; adSoyad: string; }
interface EtiketOzet { etiketID: number; ad: string; renk: string; }
interface Dokuman {
    dokumanID: number;
    baslik: string;
    aciklama?: string;
    dokumanTipi: string; // resim | pdf | docx | xlsx | video
    dosyaYolu?: string;
    orijinalDosyaAdi?: string;
    dosyaBoyutu?: number;
    videoEmbedKodu?: string;
    kullaniciID: number;
    kullaniciAd: string;
    olusturmaTarihi: string;
    guncellemeTarihi?: string;
    benim: boolean;
    etiketler: EtiketOzet[];
    paylasimlar: PaylasimKayit[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const TIP_ICONS: Record<string, string> = {
    resim: 'fas fa-image',
    pdf: 'fas fa-file-pdf',
    docx: 'fas fa-file-word',
    xlsx: 'fas fa-file-excel',
    video: 'fas fa-play-circle',
};
const TIP_COLORS: Record<string, string> = {
    resim: '#22c55e',
    pdf: '#ef4444',
    docx: '#3b82f6',
    xlsx: '#16a34a',
    video: '#8b5cf6',
};
const KABUL_TIPLERI: Record<string, string> = {
    resim: 'image/*',
    pdf: 'application/pdf',
    docx: '.docx,.doc',
    xlsx: '.xlsx,.xls',
};
const MAX_DOSYA_BOYUTU = 5 * 1024 * 1024; // 5 MB

const dosyaBoyutKontrol = (f: File): boolean => {
    if (f.size > MAX_DOSYA_BOYUTU) {
        toast.error(`Dosya boyutu en fazla 5 MB olabilir. Seçilen dosya: ${(f.size / 1024 / 1024).toFixed(2)} MB`);
        return false;
    }
    return true;
};

const formatBoyut = (b?: number): string => {
    if (!b) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
};
const fileUrl = (yol?: string) => yol ? `${API_BASE}${yol}` : '';

// ─── Component ────────────────────────────────────────────────────────────────
const Dokumanlar: React.FC = () => {
    const { user } = useAuth();

    // data
    const [dokumanlar, setDokumanlar] = useState<Dokuman[]>([]);
    const [etiketler, setEtiketler] = useState<Etiket[]>([]);
    const [kullanicilar, setKullanicilar] = useState<KullaniciOzet[]>([]);
    const [yukleniyor, setYukleniyor] = useState(false);

    // filters
    const [filtreTab, setFiltreTab] = useState<'tum' | 'benim' | 'paylasilan'>('tum');
    const [seciliEtiket, setSeciliEtiket] = useState<number | null>(null);
    const [seciliTip, setSeciliTip] = useState<string>('');
    const [aramaMetni, setAramaMetni] = useState('');

    // upload modal
    const [showUpload, setShowUpload] = useState(false);
    const [uploadTip, setUploadTip] = useState<string>('resim');
    const [uploadBaslik, setUploadBaslik] = useState('');
    const [uploadAciklama, setUploadAciklama] = useState('');
    const [uploadEtiketler, setUploadEtiketler] = useState<number[]>([]);
    const [uploadVideoEmbed, setUploadVideoEmbed] = useState('');
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [uploadDragOver, setUploadDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // viewer modal
    const [viewerDokuman, setViewerDokuman] = useState<Dokuman | null>(null);

    // edit modal
    const [editDokuman, setEditDokuman] = useState<Dokuman | null>(null);
    const [editBaslik, setEditBaslik] = useState('');
    const [editAciklama, setEditAciklama] = useState('');
    const [editEtiketler, setEditEtiketler] = useState<number[]>([]);
    const [editKaydediliyor, setEditKaydediliyor] = useState(false);

    // share modal
    const [shareDokuman, setShareDokuman] = useState<Dokuman | null>(null);
    const [shareArama, setShareArama] = useState('');
    const [shareKullanicilar, setShareKullanicilar] = useState<KullaniciOzet[]>([]);

    // ── Load data ──────────────────────────────────────────────────────────────
    const yukle = useCallback(async () => {
        setYukleniyor(true);
        try {
            const params: Record<string, string> = {};
            if (filtreTab !== 'tum') params.filtre = filtreTab;
            if (seciliEtiket) params.etiketID = String(seciliEtiket);
            const res = await axiosInstance.get('/api/Dokumanlar', { params });
            setDokumanlar(res.data);
        } catch { toast.error('Dokümanlar yüklenemedi.'); }
        finally { setYukleniyor(false); }
    }, [filtreTab, seciliEtiket]);

    useEffect(() => { yukle(); }, [yukle]);

    useEffect(() => {
        axiosInstance.get('/api/Etiketler').then((r: any) => setEtiketler(r.data)).catch(() => {});
        axiosInstance.get('/api/Kullanicilar').then((r: any) => setKullanicilar(r.data)).catch(() => {});
    }, []);

    // ── Filtered list ──────────────────────────────────────────────────────────
    const gorunenDokumanlar = dokumanlar.filter(d => {
        if (seciliTip && d.dokumanTipi !== seciliTip) return false;
        if (aramaMetni && !d.baslik.toLowerCase().includes(aramaMetni.toLowerCase())) return false;
        return true;
    });

    // ── Upload ─────────────────────────────────────────────────────────────────
    const resetUpload = () => {
        setUploadTip('resim'); setUploadBaslik(''); setUploadAciklama('');
        setUploadEtiketler([]); setUploadVideoEmbed(''); setUploadFile(null);
    };

    const handleUpload = async () => {
        if (!uploadBaslik.trim()) { toast.warning('Başlık zorunludur.'); return; }
        if (uploadTip !== 'video' && !uploadFile) { toast.warning('Lütfen dosya seçin.'); return; }
        if (uploadTip !== 'video' && uploadFile && uploadFile.size > MAX_DOSYA_BOYUTU) {
            toast.error(`Dosya boyutu en fazla 5 MB olabilir. Seçilen dosya: ${(uploadFile.size / 1024 / 1024).toFixed(2)} MB`);
            return;
        }
        if (uploadTip === 'video' && !uploadVideoEmbed.trim()) { toast.warning('Video embed kodu zorunludur.'); return; }

        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('baslik', uploadBaslik.trim());
            fd.append('aciklama', uploadAciklama);
            fd.append('dokumanTipi', uploadTip);
            if (uploadTip !== 'video' && uploadFile) fd.append('file', uploadFile);
            if (uploadTip === 'video') fd.append('videoEmbedKodu', uploadVideoEmbed);
            fd.append('etiketIDler', JSON.stringify(uploadEtiketler));

            await axiosInstance.post('/api/Dokumanlar/upload', fd);
            toast.success('Doküman yüklendi.');
            setShowUpload(false);
            resetUpload();
            yukle();
        } catch { toast.error('Yükleme başarısız.'); }
        finally { setUploading(false); }
    };

    // ── Edit ───────────────────────────────────────────────────────────────────
    const openEdit = (d: Dokuman) => {
        setEditDokuman(d);
        setEditBaslik(d.baslik);
        setEditAciklama(d.aciklama ?? '');
        setEditEtiketler(d.etiketler.map(e => e.etiketID));
    };
    const handleEdit = async () => {
        if (!editDokuman) return;
        setEditKaydediliyor(true);
        try {
            await axiosInstance.put(`/api/Dokumanlar/${editDokuman.dokumanID}`, {
                baslik: editBaslik, aciklama: editAciklama, etiketIDler: editEtiketler,
            });
            toast.success('Güncellendi.');
            setEditDokuman(null);
            yukle();
        } catch { toast.error('Güncellenemedi.'); }
        finally { setEditKaydediliyor(false); }
    };

    // ── Delete ─────────────────────────────────────────────────────────────────
    const handleDelete = async (d: Dokuman) => {
        if (!window.confirm(`"${d.baslik}" silinecek. Emin misiniz?`)) return;
        try {
            await axiosInstance.delete(`/api/Dokumanlar/${d.dokumanID}`);
            toast.success('Doküman silindi.');
            yukle();
        } catch { toast.error('Silinemedi.'); }
    };

    // ── Share ──────────────────────────────────────────────────────────────────
    const openShare = (d: Dokuman) => { setShareDokuman(d); setShareArama(''); };
    const handleShare = async (kullaniciId: number) => {
        if (!shareDokuman) return;
        try {
            await axiosInstance.post(`/api/Dokumanlar/${shareDokuman.dokumanID}/paylas`, { kullaniciID: kullaniciId });
            toast.success('Paylaşıldı.');
            // Update local state
            setShareDokuman(prev => prev ? {
                ...prev,
                paylasimlar: [...prev.paylasimlar, {
                    id: 0,
                    paylasilanKullaniciID: kullaniciId,
                    adSoyad: kullanicilar.find(k => k.kullaniciID === kullaniciId)?.adSoyad ?? '',
                }],
            } : null);
            yukle();
        } catch (e: any) {
            toast.error(e?.response?.data?.message ?? 'Paylaşılamadı.');
        }
    };
    const handleShareKaldir = async (kullaniciId: number) => {
        if (!shareDokuman) return;
        try {
            await axiosInstance.delete(`/api/Dokumanlar/${shareDokuman.dokumanID}/paylas/${kullaniciId}`);
            toast.success('Paylaşım kaldırıldı.');
            setShareDokuman(prev => prev ? {
                ...prev,
                paylasimlar: prev.paylasimlar.filter(p => p.paylasilanKullaniciID !== kullaniciId),
            } : null);
            yukle();
        } catch { toast.error('İşlem başarısız.'); }
    };

    const filtreliKullanicilar = kullanicilar.filter(k =>
        k.kullaniciID !== user?.kullaniciID &&
        k.adSoyad.toLowerCase().includes(shareArama.toLowerCase())
    );

    // ── Toggle etiket helper ───────────────────────────────────────────────────
    const toggleEtiket = (ids: number[], setIds: (v: number[]) => void, id: number) => {
        setIds(ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
    };

    // ─────────────────────────────────────────────────────────────────────────
    const S = styles;

    return (
        <div style={S.page}>
            {/* ── Header ──────────────────────────────────────────────────────── */}
            <div style={S.header}>
                <div>
                    <h1 style={S.title}><i className="fas fa-folder-open" style={{ marginRight: 10, color: '#e67d21' }} />Dokümanlar</h1>
                    <p style={S.subtitle}>Belgelerinizi yönetin, düzenleyin ve ekip arkadaşlarınızla paylaşın.</p>
                </div>
                <button onClick={() => { resetUpload(); setShowUpload(true); }} style={S.btnOrange}>
                    <i className="fas fa-plus" style={{ marginRight: 7 }} /> Yeni Doküman
                </button>
            </div>

            {/* ── Filtreler ───────────────────────────────────────────────────── */}
            <div style={S.filterBar}>
                {/* Tab filtre */}
                <div style={S.tabGroup}>
                    {(['tum', 'benim', 'paylasilan'] as const).map(t => (
                        <button key={t} onClick={() => setFiltreTab(t)}
                            style={{ ...S.tab, ...(filtreTab === t ? S.tabActive : {}) }}>
                            {t === 'tum' ? 'Tümü' : t === 'benim' ? 'Benim' : 'Paylaşılan'}
                        </button>
                    ))}
                </div>
                {/* Tip filter */}
                <select value={seciliTip} onChange={e => setSeciliTip(e.target.value)} style={S.select}>
                    <option value="">Tüm tipler</option>
                    <option value="resim">Resim</option>
                    <option value="pdf">PDF</option>
                    <option value="docx">Word</option>
                    <option value="xlsx">Excel</option>
                    <option value="video">Video</option>
                </select>
                {/* Etiket filter */}
                <select value={seciliEtiket ?? ''} onChange={e => setSeciliEtiket(e.target.value ? Number(e.target.value) : null)} style={S.select}>
                    <option value="">Tüm etiketler</option>
                    {etiketler.map(e => <option key={e.etiketID} value={e.etiketID}>{e.ad}</option>)}
                </select>
                {/* Arama */}
                <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
                    <i className="fas fa-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 13 }} />
                    <input type="text" placeholder="Başlıkta ara..." value={aramaMetni}
                        onChange={e => setAramaMetni(e.target.value)}
                        style={{ ...S.select, paddingLeft: 32, width: '100%' }} />
                </div>
            </div>

            {/* ── Grid ────────────────────────────────────────────────────────── */}
            {yukleniyor ? (
                <div style={S.emptyState}>Yükleniyor...</div>
            ) : gorunenDokumanlar.length === 0 ? (
                <div style={S.emptyState}>
                    <i className="fas fa-folder-open" style={{ fontSize: 36, color: '#cbd5e1', marginBottom: 10 }} />
                    <div>Doküman bulunamadı.</div>
                </div>
            ) : (
                <div style={S.grid}>
                    {gorunenDokumanlar.map(d => (
                        <DokumanKarti
                            key={d.dokumanID}
                            dokuman={d}
                            onView={() => setViewerDokuman(d)}
                            onEdit={() => openEdit(d)}
                            onDelete={() => handleDelete(d)}
                            onShare={() => openShare(d)}
                        />
                    ))}
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════════════
                UPLOAD MODAL
            ══════════════════════════════════════════════════════════════════ */}
            {showUpload && (
                <Modal title="Yeni Doküman Ekle" onClose={() => { setShowUpload(false); resetUpload(); }} width={580}>
                    {/* Tip seçimi */}
                    <label style={S.label}>Doküman Tipi</label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                        {['resim', 'pdf', 'docx', 'xlsx', 'video'].map(t => (
                            <button key={t} onClick={() => setUploadTip(t)}
                                style={{
                                    ...S.tipBtn,
                                    background: uploadTip === t ? TIP_COLORS[t] : '#f1f5f9',
                                    color: uploadTip === t ? '#fff' : '#475569',
                                    border: `2px solid ${uploadTip === t ? TIP_COLORS[t] : '#e2e8f0'}`,
                                }}>
                                <i className={TIP_ICONS[t]} style={{ marginRight: 5 }} />
                                {t === 'resim' ? 'Resim' : t === 'pdf' ? 'PDF' : t === 'docx' ? 'Word' : t === 'xlsx' ? 'Excel' : 'Video'}
                            </button>
                        ))}
                    </div>

                    {/* Başlık */}
                    <label style={S.label}>Başlık *</label>
                    <input value={uploadBaslik} onChange={e => setUploadBaslik(e.target.value)}
                        placeholder="Doküman başlığı..." style={{ ...S.input, marginBottom: 12 }} />

                    {/* Açıklama */}
                    <label style={S.label}>Açıklama</label>
                    <textarea value={uploadAciklama} onChange={e => setUploadAciklama(e.target.value)}
                        placeholder="İsteğe bağlı..." rows={2}
                        style={{ ...S.input, resize: 'vertical', marginBottom: 12 }} />

                    {/* Dosya veya video embed */}
                    {uploadTip === 'video' ? (
                        <>
                            <label style={S.label}>Video Embed Kodu *</label>
                            <textarea value={uploadVideoEmbed} onChange={e => setUploadVideoEmbed(e.target.value)}
                                placeholder={'<iframe src="https://www.youtube.com/embed/..." ...></iframe>'}
                                rows={4} style={{ ...S.input, fontFamily: 'monospace', fontSize: 12, resize: 'vertical', marginBottom: 12 }} />
                        </>
                    ) : (
                        <>
                            <label style={S.label}>Dosya *</label>
                            <div
                                onDragOver={e => { e.preventDefault(); setUploadDragOver(true); }}
                                onDragLeave={() => setUploadDragOver(false)}
                                onDrop={e => {
                                    e.preventDefault(); setUploadDragOver(false);
                                    const f = e.dataTransfer.files[0];
                                    if (f && dosyaBoyutKontrol(f)) setUploadFile(f);
                                }}
                                onClick={() => fileInputRef.current?.click()}
                                style={{
                                    border: `2px dashed ${uploadDragOver ? '#e67d21' : '#cbd5e1'}`,
                                    borderRadius: 10, padding: '20px 16px', textAlign: 'center',
                                    cursor: 'pointer', marginBottom: 12, background: uploadDragOver ? '#fff7ed' : '#f8fafc',
                                    transition: 'all .2s',
                                }}
                            >
                                <input ref={fileInputRef} type="file"
                                    accept={KABUL_TIPLERI[uploadTip] ?? '*/*'}
                                    style={{ display: 'none' }}
                                    onChange={e => { const f = e.target.files?.[0]; if (f && dosyaBoyutKontrol(f)) setUploadFile(f); e.target.value = ''; }} />
                                {uploadFile ? (
                                    <span style={{ color: '#1e293b', fontWeight: 600 }}>
                                        <i className="fas fa-check-circle" style={{ color: '#22c55e', marginRight: 6 }} />
                                        {uploadFile.name} ({formatBoyut(uploadFile.size)})
                                    </span>
                                ) : (
                                    <span style={{ color: '#94a3b8', fontSize: 13.5 }}>
                                        <i className="fas fa-upload" style={{ marginRight: 6 }} />
                                        Sürükle &amp; bırak veya tıkla <span style={{ fontSize: 12, color: '#cbd5e1' }}>(maks. 5 MB)</span>
                                    </span>
                                )}
                            </div>
                        </>
                    )}

                    {/* Etiketler */}
                    <label style={S.label}>Etiketler</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
                        {etiketler.map(e => {
                            const sec = uploadEtiketler.includes(e.etiketID);
                            return (
                                <button key={e.etiketID}
                                    onClick={() => toggleEtiket(uploadEtiketler, setUploadEtiketler, e.etiketID)}
                                    style={{
                                        ...S.etiketChip,
                                        background: sec ? e.renk : '#f1f5f9',
                                        color: sec ? '#fff' : '#475569',
                                        border: `1.5px solid ${sec ? e.renk : '#e2e8f0'}`,
                                    }}>
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: sec ? '#fff' : e.renk, display: 'inline-block', marginRight: 5 }} />
                                    {e.ad}
                                </button>
                            );
                        })}
                        {etiketler.length === 0 && <span style={{ color: '#94a3b8', fontSize: 12.5 }}>Henüz etiket tanımlanmamış (Ayarlar &gt; Sistem Ayarları)</span>}
                    </div>

                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                        <button onClick={() => { setShowUpload(false); resetUpload(); }} style={S.btnSecondary}>İptal</button>
                        <button onClick={handleUpload} disabled={uploading} style={S.btnOrange}>
                            {uploading ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }} />Yükleniyor...</> : <><i className="fas fa-upload" style={{ marginRight: 6 }} />Yükle</>}
                        </button>
                    </div>
                </Modal>
            )}

            {/* ══════════════════════════════════════════════════════════════════
                VIEWER MODAL
            ══════════════════════════════════════════════════════════════════ */}
            {viewerDokuman && (
                <Modal title={viewerDokuman.baslik} onClose={() => setViewerDokuman(null)} width={820}
                    titleIcon={<i className={TIP_ICONS[viewerDokuman.dokumanTipi]} style={{ color: TIP_COLORS[viewerDokuman.dokumanTipi], marginRight: 8 }} />}>
                    {viewerDokuman.dokumanTipi === 'resim' && (
                        <div style={{ textAlign: 'center', padding: '8px 0' }}>
                            <img src={fileUrl(viewerDokuman.dosyaYolu)} alt={viewerDokuman.baslik}
                                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8 }} />
                        </div>
                    )}
                    {viewerDokuman.dokumanTipi === 'pdf' && (
                        <iframe src={fileUrl(viewerDokuman.dosyaYolu)} title={viewerDokuman.baslik}
                            style={{ width: '100%', height: '70vh', border: 'none', borderRadius: 8 }} />
                    )}
                    {viewerDokuman.dokumanTipi === 'video' && viewerDokuman.videoEmbedKodu && (
                        <div style={{ borderRadius: 10, overflow: 'hidden', background: '#000' }}
                            dangerouslySetInnerHTML={{ __html: viewerDokuman.videoEmbedKodu
                                .replace(/width="[^"]*"/g, 'width="100%"')
                                .replace(/style="[^"]*"/g, 'style="width:100%;height:420px;border:none;"') }} />
                    )}
                    {(viewerDokuman.dokumanTipi === 'docx' || viewerDokuman.dokumanTipi === 'xlsx') && (
                        <div style={{ padding: '24px 0', textAlign: 'center' }}>
                            <i className={TIP_ICONS[viewerDokuman.dokumanTipi]}
                                style={{ fontSize: 48, color: TIP_COLORS[viewerDokuman.dokumanTipi], marginBottom: 12 }} />
                            <p style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>
                                {viewerDokuman.orijinalDosyaAdi ?? viewerDokuman.baslik}
                            </p>
                            <a href={fileUrl(viewerDokuman.dosyaYolu)} download={viewerDokuman.orijinalDosyaAdi ?? 'dosya'}
                                style={{ ...S.btnOrange, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                                <i className="fas fa-download" /> İndir
                            </a>
                        </div>
                    )}
                    {/* Meta bilgi */}
                    <div style={{ marginTop: 16, padding: '12px 0 0', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                        <span style={S.metaItem}><i className="fas fa-user" style={{ marginRight: 5 }} />{viewerDokuman.kullaniciAd}</span>
                        <span style={S.metaItem}><i className="fas fa-calendar" style={{ marginRight: 5 }} />{new Date(viewerDokuman.olusturmaTarihi).toLocaleDateString('tr-TR')}</span>
                        {viewerDokuman.dosyaBoyutu && <span style={S.metaItem}>{formatBoyut(viewerDokuman.dosyaBoyutu)}</span>}
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {viewerDokuman.etiketler.map(e => (
                                <span key={e.etiketID} style={{ ...S.etiketBadge, background: e.renk }}>{e.ad}</span>
                            ))}
                        </div>
                    </div>
                </Modal>
            )}

            {/* ══════════════════════════════════════════════════════════════════
                EDIT MODAL
            ══════════════════════════════════════════════════════════════════ */}
            {editDokuman && (
                <Modal title="Dokümanı Düzenle" onClose={() => setEditDokuman(null)} width={500}>
                    <label style={S.label}>Başlık *</label>
                    <input value={editBaslik} onChange={e => setEditBaslik(e.target.value)}
                        style={{ ...S.input, marginBottom: 12 }} />

                    <label style={S.label}>Açıklama</label>
                    <textarea value={editAciklama} onChange={e => setEditAciklama(e.target.value)}
                        rows={2} style={{ ...S.input, resize: 'vertical', marginBottom: 12 }} />

                    <label style={S.label}>Etiketler</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
                        {etiketler.map(e => {
                            const sec = editEtiketler.includes(e.etiketID);
                            return (
                                <button key={e.etiketID}
                                    onClick={() => toggleEtiket(editEtiketler, setEditEtiketler, e.etiketID)}
                                    style={{
                                        ...S.etiketChip,
                                        background: sec ? e.renk : '#f1f5f9',
                                        color: sec ? '#fff' : '#475569',
                                        border: `1.5px solid ${sec ? e.renk : '#e2e8f0'}`,
                                    }}>
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: sec ? '#fff' : e.renk, display: 'inline-block', marginRight: 5 }} />
                                    {e.ad}
                                </button>
                            );
                        })}
                    </div>

                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                        <button onClick={() => setEditDokuman(null)} style={S.btnSecondary}>İptal</button>
                        <button onClick={handleEdit} disabled={editKaydediliyor} style={S.btnOrange}>
                            {editKaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
                        </button>
                    </div>
                </Modal>
            )}

            {/* ══════════════════════════════════════════════════════════════════
                SHARE MODAL
            ══════════════════════════════════════════════════════════════════ */}
            {shareDokuman && (
                <Modal title={`Paylaş — ${shareDokuman.baslik}`} onClose={() => setShareDokuman(null)} width={460}>
                    {shareDokuman.paylasimlar.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                            <label style={S.label}>Paylaşılan Kullanıcılar</label>
                            {shareDokuman.paylasimlar.map(p => (
                                <div key={p.paylasilanKullaniciID} style={S.shareRow}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <div style={S.avatar}>{p.adSoyad.charAt(0).toUpperCase()}</div>
                                        <span style={{ fontSize: 13.5, color: '#1e293b' }}>{p.adSoyad}</span>
                                    </div>
                                    <button onClick={() => handleShareKaldir(p.paylasilanKullaniciID)}
                                        style={{ ...S.btnSecondary, padding: '4px 10px', fontSize: 12, color: '#ef4444', borderColor: '#fecaca' }}>
                                        Kaldır
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <label style={S.label}>Kullanıcı Ekle</label>
                    <input placeholder="İsimle ara..." value={shareArama}
                        onChange={e => setShareArama(e.target.value)}
                        style={{ ...S.input, marginBottom: 8 }} />

                    <div style={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {filtreliKullanicilar
                            .filter(k => !shareDokuman.paylasimlar.some(p => p.paylasilanKullaniciID === k.kullaniciID))
                            .map(k => (
                                <div key={k.kullaniciID} style={S.shareRow}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <div style={S.avatar}>{k.adSoyad.charAt(0).toUpperCase()}</div>
                                        <span style={{ fontSize: 13.5, color: '#1e293b' }}>{k.adSoyad}</span>
                                    </div>
                                    <button onClick={() => handleShare(k.kullaniciID)} style={S.btnShareAdd}>
                                        <i className="fas fa-plus" style={{ marginRight: 4 }} />Ekle
                                    </button>
                                </div>
                            ))}
                        {filtreliKullanicilar.filter(k => !shareDokuman.paylasimlar.some(p => p.paylasilanKullaniciID === k.kullaniciID)).length === 0 && (
                            <p style={{ color: '#94a3b8', fontSize: 12.5, padding: '8px 0' }}>Kullanıcı bulunamadı.</p>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    );
};

// ─── DokumanKarti ──────────────────────────────────────────────────────────────
interface KartiProps {
    dokuman: Dokuman;
    onView: () => void;
    onEdit: () => void;
    onDelete: () => void;
    onShare: () => void;
}
const DokumanKarti: React.FC<KartiProps> = ({ dokuman: d, onView, onEdit, onDelete, onShare }) => {
    const tipRenk = TIP_COLORS[d.dokumanTipi] ?? '#64748b';
    const tipIcon = TIP_ICONS[d.dokumanTipi] ?? 'fas fa-file';
    const isImage = d.dokumanTipi === 'resim' && d.dosyaYolu;

    return (
        <div style={styles.kart} onClick={onView}>
            {/* Önizleme */}
            <div style={{ height: 130, background: '#f1f5f9', borderRadius: '10px 10px 0 0', overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                {isImage ? (
                    <img src={fileUrl(d.dosyaYolu)} alt={d.baslik}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                    <i className={tipIcon} style={{ fontSize: 44, color: tipRenk, opacity: .85 }} />
                )}
                {/* Tip badge */}
                <span style={{ position: 'absolute', top: 8, left: 8, background: tipRenk, color: '#fff',
                    borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700, letterSpacing: .3 }}>
                    {d.dokumanTipi.toUpperCase()}
                </span>
                {!d.benim && (
                    <span style={{ position: 'absolute', top: 8, right: 8, background: '#6b7280', color: '#fff',
                        borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 600 }}>
                        Paylaşılan
                    </span>
                )}
            </div>

            {/* İçerik */}
            <div style={{ padding: '12px 14px 10px' }}>
                <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: '#0f172a',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.baslik}>
                    {d.baslik}
                </h3>
                {d.aciklama && (
                    <p style={{ margin: '0 0 6px', fontSize: 12.5, color: '#64748b', overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.aciklama}</p>
                )}
                {/* Etiketler */}
                {d.etiketler.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                        {d.etiketler.slice(0, 3).map(e => (
                            <span key={e.etiketID} style={{ ...styles.etiketBadge, background: e.renk }}>{e.ad}</span>
                        ))}
                        {d.etiketler.length > 3 && (
                            <span style={{ ...styles.etiketBadge, background: '#94a3b8' }}>+{d.etiketler.length - 3}</span>
                        )}
                    </div>
                )}
                {/* Meta */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>
                        {d.benim ? 'Ben' : d.kullaniciAd} · {new Date(d.olusturmaTarihi).toLocaleDateString('tr-TR')}
                    </span>
                    {d.dosyaBoyutu ? <span style={{ fontSize: 11, color: '#94a3b8' }}>{formatBoyut(d.dosyaBoyutu)}</span> : null}
                </div>
            </div>

            {/* Aksiyonlar */}
            {d.benim && (
                <div style={{ padding: '8px 14px 12px', display: 'flex', gap: 6, borderTop: '1px solid #f1f5f9' }}
                    onClick={e => e.stopPropagation()}>
                    <button onClick={onShare} style={styles.actionBtn} title="Paylaş">
                        <i className="fas fa-share-alt" />
                    </button>
                    <button onClick={onEdit} style={styles.actionBtn} title="Düzenle">
                        <i className="fas fa-pen" />
                    </button>
                    <button onClick={onDelete} style={{ ...styles.actionBtn, color: '#ef4444' }} title="Sil">
                        <i className="fas fa-trash" />
                    </button>
                    {d.paylasimlar.length > 0 && (
                        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <i className="fas fa-users" /> {d.paylasimlar.length} kişiyle paylaşıldı
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

// ─── Modal ─────────────────────────────────────────────────────────────────────
const Modal: React.FC<{
    title: string; onClose: () => void; width?: number; children: React.ReactNode;
    titleIcon?: React.ReactNode;
}> = ({ title, onClose, width = 540, children, titleIcon }) => (
    <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(3px)', padding: 16,
    }} onClick={onClose}>
        <div style={{
            background: '#fff', borderRadius: 14, width: '100%', maxWidth: width,
            maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.25)',
        }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center' }}>
                    {titleIcon}{title}
                </h2>
                <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 18, lineHeight: 1, padding: 4 }}>✕</button>
            </div>
            <div style={{ padding: '18px 22px' }}>{children}</div>
        </div>
    </div>
);

// ─── Styles ────────────────────────────────────────────────────────────────────
const styles = {
    page: { padding: '28px 32px', background: '#f4f6fb', minHeight: '100vh', fontFamily: "'Inter','Segoe UI',sans-serif" } as React.CSSProperties,
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22, flexWrap: 'wrap' as const, gap: 12 },
    title: { fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' } as React.CSSProperties,
    subtitle: { fontSize: 13.5, color: '#64748b', margin: '4px 0 0' } as React.CSSProperties,
    filterBar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 22, flexWrap: 'wrap' as const, background: '#fff', borderRadius: 10, padding: '12px 16px', border: '1px solid #e5e7eb' },
    tabGroup: { display: 'flex', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' },
    tab: { padding: '7px 16px', border: 'none', background: '#fff', fontSize: 13.5, cursor: 'pointer', color: '#64748b', fontWeight: 500 } as React.CSSProperties,
    tabActive: { background: '#1a2440', color: '#fff', fontWeight: 700 } as React.CSSProperties,
    select: { padding: '7px 11px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13.5, color: '#334155', background: '#fff', outline: 'none', cursor: 'pointer' } as React.CSSProperties,
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 18 } as React.CSSProperties,
    kart: { background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', cursor: 'pointer', transition: 'box-shadow .2s, transform .15s', boxShadow: '0 1px 4px rgba(0,0,0,.05)' } as React.CSSProperties,
    emptyState: { display: 'flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'center', padding: '60px 0', color: '#94a3b8', fontSize: 14, gap: 4 },
    btnOrange: { background: '#e67d21', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 } as React.CSSProperties,
    btnSecondary: { background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 16px', fontSize: 13.5, cursor: 'pointer', fontWeight: 500 } as React.CSSProperties,
    btnShareAdd: { background: '#eff6ff', color: '#3b82f6', border: '1px solid #bfdbfe', borderRadius: 7, padding: '5px 12px', fontSize: 12.5, cursor: 'pointer', fontWeight: 600 } as React.CSSProperties,
    label: { display: 'block', fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 5, textTransform: 'uppercase' as const, letterSpacing: '.04em' },
    input: { width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, color: '#1e293b', outline: 'none', boxSizing: 'border-box' as const, fontFamily: 'inherit' } as React.CSSProperties,
    tipBtn: { padding: '7px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 600, transition: 'all .15s' } as React.CSSProperties,
    etiketChip: { padding: '5px 12px', borderRadius: 20, fontSize: 12.5, cursor: 'pointer', fontWeight: 500, display: 'flex', alignItems: 'center' } as React.CSSProperties,
    etiketBadge: { display: 'inline-block', borderRadius: 20, padding: '2px 8px', fontSize: 11, color: '#fff', fontWeight: 600 } as React.CSSProperties,
    metaItem: { fontSize: 12.5, color: '#64748b', display: 'flex', alignItems: 'center' } as React.CSSProperties,
    actionBtn: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', color: '#475569', fontSize: 13 } as React.CSSProperties,
    shareRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px', borderRadius: 8, border: '1px solid #f1f5f9', background: '#fafafa' } as React.CSSProperties,
    avatar: { width: 30, height: 30, borderRadius: '50%', background: '#1a2440', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700 } as React.CSSProperties,
};

export default Dokumanlar;
